import { nextTick, onUnmounted, watch } from 'vue';
import type { Ref } from 'vue';
import { useAutopilotStore } from '@llmor/autopilot-vue';

export interface AutopilotFormConfig {
  /** Reactive autopilot key. When empty the form opts out and nothing is registered. */
  key: () => string | undefined;
  /** Reactive description fed into the LLM context to explain what the form edits. */
  description: () => string | undefined;
  /** Maps the current form object into the shape surfaced as `form` in context. Defaults to the raw object. */
  transformer?: (object: any) => Record<string, any>;
  /**
   * Describes the editable fields as JSON-schema properties so the LLM knows what `set_fields`
   * accepts. When omitted, `set_fields` takes an open object and the LLM mirrors the keys it sees
   * in `context.${key}.form`.
   */
  fields?: () => Record<string, any>;
  /** The editable form object (rfObject). */
  object: Ref<any>;
  /** Current validation errors keyed by field name (rfErrors). */
  errors: Ref<Record<string, string[]>>;
  /** Whether the form is creating a new entity (rfIsNewObject). */
  isNew: Ref<boolean>;
  /** Whether a fetch/save is in flight (rfIsLoading). */
  isLoading: Ref<boolean>;
  /** Applies a patch to the form object (in memory only — does not persist). */
  setFields: (patch: Record<string, any>) => void;
  /** Persists the form; resolves once the save round-trip finishes. */
  save: () => Promise<void> | void;
  /** Gate the save function behind a user confirmation prompt (rendered by `AutopilotBar.vue`). */
  saveSensitive?: boolean;
}

/**
 * Exposes a remote form to autopilot: while the owning component is set up it publishes
 * `context[key]` (description + transformed form values + validation errors + state) and registers
 * `${key}_set_fields(...)` and `${key}_save()` functions. Opt-in — does nothing while `key()` is
 * empty.
 *
 * Functions are registered during setup (not onMounted) so a navigating function like
 * `goto_create_account` can be held by route-readiness until this form's tools already exist.
 * Releasing on the next tick after setup would otherwise race the mount hook and the assistant
 * would see a page with no `_set_fields` / `_save`.
 *
 * Context is reassigned as a fresh object on every change because the lib diffs by reference;
 * mutating in place would not sync.
 */
export function useAutopilotForm(cfg: AutopilotFormConfig): void {
  const store = useAutopilotStore();

  const functionNames: string[] = [];
  let stopWatch: (() => void) | undefined;

  // withholds the function response until the post-mutation context republish has flushed. the deep
  // watcher below republishes context[key] on the next tick; after that the state-sync gate commits it
  // to the relay before the response goes out — so the assistant never reads pre-mutation state.
  const withResolution = async <T>(label: string, work: () => Promise<T> | T): Promise<T> => {
    const release = store.client.beginContextResolution(label);
    try {
      return await work();
    } finally {
      await nextTick();
      release();
    }
  };

  const key = cfg.key();
  if (key) {
    const publish = () => {
      store.context[key as string] = {
        description: cfg.description(),
        form: cfg.transformer ? cfg.transformer(cfg.object.value) : cfg.object.value,
        errors: cfg.errors.value,
        is_new: cfg.isNew.value,
        is_saving: cfg.isLoading.value,
      };
    };

    const hasFields = !!cfg.fields;
    const setFieldsName = `${key}_set_fields`;
    store.client.registerFunction({
      name: setFieldsName,
      description:
        `Set one or more fields on the "${key}" form. Edits happen in memory only — call ` +
        `${key}_save to persist. The form's current values are in context.${key}.form` +
        (hasFields ? '.' : '; set the same keys you see there.'),
      parameters: {
        type: 'object',
        properties: hasFields ? cfg.fields!() : {},
        // when the editable fields are described, constrain to them; otherwise accept any key
        additionalProperties: !hasFields,
      },
      handler: async (rawArgs) => {
        const patch = (rawArgs ?? {}) as Record<string, any>;
        return withResolution(setFieldsName, () => {
          cfg.setFields(patch);
          // republish immediately so transformer side-effects (e.g. password_set) land in context
          publish();
          return { success: true, fields: Object.keys(patch) };
        });
      },
    });
    functionNames.push(setFieldsName);

    const saveName = `${key}_save`;
    store.client.registerFunction({
      name: saveName,
      description:
        `Persist the "${key}" form to the server. Any validation errors appear in ` +
        `context.${key}.errors after the call.`,
      ...(cfg.saveSensitive ? { sensitive: true } : {}),
      parameters: {
        type: 'object',
        properties: {},
      },
      handler: async () =>
        // hold the response until the post-save form/errors context has been republished and synced
        withResolution(saveName, async () => {
          // the save action may reject on a failed request (e.g. validation); prefer reporting the
          // field errors back to the assistant over surfacing the raw rejection.
          let threw: unknown;
          try {
            await cfg.save();
          } catch (e) {
            threw = e;
          }
          publish();
          const errors = cfg.errors.value ?? {};
          if (Object.keys(errors).length > 0) return { success: false, errors };
          if (threw) {
            const message = threw instanceof Error ? threw.message : 'Save failed';
            return { success: false, error: message };
          }
          return { success: true };
        }),
    });
    functionNames.push(saveName);

    stopWatch = watch([cfg.object, cfg.errors, cfg.isNew, cfg.isLoading], publish, {
      immediate: true,
      deep: true,
    });
  }

  onUnmounted(() => {
    stopWatch?.();
    for (const name of functionNames) store.client.unregisterFunction(name);
    if (key) delete store.context[key];
  });
}
